import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import DiaryEntryList from './DiaryEntryList';
import DiarySummary from './DiarySummary';
import DiaryGoals from './DiaryGoals';
import './Diary.css';

// Coach view of a gymnast's diary: the on/off switch, goals, the summary and the
// entries (read-only; only the gymnast writes in their diary).
export default function GymnastDiaryTab({ gymnastId }) {
  const [diary, setDiary] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    axios.get(`/api/training-diary/gymnasts/${gymnastId}/entries`)
      .then(res => setDiary(res.data))
      .catch(() => setError('Could not load the diary.'));
  }, [gymnastId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    setBusy(true);
    try {
      const res = await axios.patch(`/api/training-diary/gymnasts/${gymnastId}/diary-enabled`, { enabled: !diary.diaryEnabled });
      setDiary(d => ({ ...d, diaryEnabled: res.data.diaryEnabled, diaryByDefault: Boolean(res.data.diaryByDefault) }));
    } catch {
      setError('Could not change the diary setting.');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="card"><p className="bk-error">{error}</p></div>;
  if (!diary) return <div className="card"><p>Loading diary...</p></div>;

  return (
    <div className="diary-tab">
      <div className="card diary-tab__toggle">
        <span id="diary-toggle-label"><strong>Training diary</strong></span>
        <button
          type="button"
          role="switch"
          aria-checked={diary.diaryEnabled}
          aria-labelledby="diary-toggle-label"
          className={`btn btn-sm ${diary.diaryEnabled ? 'btn-primary' : 'btn-outline'}`}
          disabled={busy}
          onClick={toggle}
        >
          {diary.diaryEnabled ? 'On' : 'Off'}
        </button>
        <span className="diary-tab__hint">
          {diary.diaryEnabled
            ? `The gymnast can log sessions from My Diary.${diary.diaryByDefault ? ' Diaries are on for every gymnast unless turned off.' : ''}`
            : 'Turn on to let this gymnast keep a training diary. Existing entries stay visible here.'}
        </span>
      </div>
      <DiaryGoals gymnastId={gymnastId} />
      <DiarySummary gymnastId={gymnastId} />
      <div className="card">
        <h4 className="card-title">Entries</h4>
        <DiaryEntryList entries={diary.entries} canWrite={false} allowShowAll />
      </div>
    </div>
  );
}
